import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Wrench,
  RefreshCw,
  Clock,
  History,
  ChevronDown,
  ChevronRight,
  Settings2,
  Info,
  Zap,
  SlidersHorizontal,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  Card,
  FilterBar,
  FilterChip,
  Tooltip,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import { formatCurrency } from '../shared.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

type CheckStatus = 'ok' | 'issue' | 'auto_fixed' | 'skipped' | 'error'
type CheckSeverity = 'info' | 'warn' | 'error'

interface VerificationRunSummary {
  runId: string
  status: 'running' | 'done' | 'error'
  totalArticles: number
  checked: number
  issuesFound: number
  autoFixed: number
  startedAt: string
  finishedAt: string | null
  error?: string | null
}

interface VerificationIssue {
  articleId: string
  itemCode: string
  itemName: string
  groupName: string | null
  checkId: string
  status: CheckStatus
  severity: CheckSeverity
  message: string
  oldValue: string | null
  newValue: string | null
  checkedAt: string
  aiScore?: number | null
}

interface FixableItem {
  articleId: string
  checkId: string
  aiScore: number | null
  itemCode: string
  itemName: string
  message: string
  newValue: string | null
}

interface VerificationChange {
  id: string
  articleId: string
  itemCode: string
  itemName: string
  runId: string
  checkId: string
  field: string
  oldValue: string | null
  newValue: string | null
  changedAt: string
}

interface VerificationStats {
  lastRun: VerificationRunSummary | null
  issuesByCheck: Array<{ checkId: string; count: number; severity: string }>
  totalIssues: number
  totalAutoFixed: number
  articleCount?: { total: number; active: number; inactive: number }
}

type CheckId =
  | 'group_check'
  | 'purchase_price_missing'
  | 'purchase_price_egis'
  | 'sales_price_calculation'
  | 'price_group_check'
  | 'content_standard'
  | 'eol_eos'
  | 'unit_missing'
  | 'manufacturer_missing'
  | 'manufacturer_suggestion'
  | 'min_margin'
  | 'inactive_with_stock'

type CheckConfig = Record<CheckId, boolean>

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface CheckMeta {
  label: string
  description: string
  requiresExternal?: string
}

const CHECK_META: Record<CheckId, CheckMeta> = {
  group_check: {
    label: 'Warengruppe',
    description: 'KI prüft ob die Warengruppe zum Artikel passt',
    requiresExternal: 'KI (Claude)',
  },
  purchase_price_missing: {
    label: 'EK-Preis fehlt',
    description: 'Prüft ob ein Einkaufspreis hinterlegt ist',
  },
  purchase_price_egis: {
    label: 'EK via EGIS',
    description: 'Gleicht den EK-Preis mit EGIS-Distributor-Preisen ab (Δ > 10% → Auto-Fix)',
    requiresExternal: 'EGIS',
  },
  sales_price_calculation: {
    label: 'VK-Berechnung',
    description: 'Setzt den VK-Preis falls er 0 ist und eine Preisregel existiert',
    requiresExternal: 'Auto-Fix',
  },
  price_group_check: {
    label: 'Preisgruppe',
    description: 'Ordnet den Artikel einer Preisgruppe zu falls keine zugeordnet ist',
    requiresExternal: 'Auto-Fix',
  },
  content_standard: {
    label: 'Titel & Beschreibung',
    description: 'Prüft ob Titel/Beschreibung dem Content-Standard entsprechen',
    requiresExternal: 'KI (Claude)',
  },
  eol_eos: {
    label: 'EOL/EOS',
    description: 'Prüft EOL/EOS-Datum; liest EOL-Flag aus EGIS (Auto-Fix via EGIS möglich)',
    requiresExternal: 'EGIS (optional)',
  },
  unit_missing: { label: 'Maßeinheit', description: 'Prüft ob eine Maßeinheit hinterlegt ist' },
  manufacturer_missing: {
    label: 'Hersteller fehlt',
    description: 'Prüft ob ein Hersteller hinterlegt ist',
  },
  manufacturer_suggestion: {
    label: 'Hersteller-Vorschlag',
    description: 'KI analysiert Artikelname/-code und schlägt einen Hersteller vor (Auto-Fix ab 85 % Konfidenz)',
    requiresExternal: 'KI (Claude)',
  },
  min_margin: { label: 'Mindestmarge', description: 'Warnt wenn die Marge unter 5 % liegt' },
  inactive_with_stock: {
    label: 'Inaktiv mit Lager',
    description: 'Warnt wenn ein inaktiver Artikel noch Lagerbestand hat',
  },
}

const ALL_CHECK_IDS = Object.keys(CHECK_META) as CheckId[]

const CHECK_LABELS: Record<string, string> = Object.fromEntries(
  ALL_CHECK_IDS.map((id) => [id, CHECK_META[id].label]),
)

const FIXABLE_CHECKS = new Set([
  'group_check',
  'purchase_price_egis',
  'sales_price_calculation',
  'price_group_check',
  'manufacturer_suggestion',
  'content_standard',
  'eol_eos',
])

function SeverityBadge({ severity }: { severity: CheckSeverity }) {
  if (severity === 'error')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        Kritisch
      </span>
    )
  if (severity === 'warn')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle className="w-3 h-3" />
        Warnung
      </span>
    )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
      <AlertTriangle className="w-3 h-3" />
      Info
    </span>
  )
}

function StatusBadge({ status }: { status: CheckStatus }) {
  if (status === 'auto_fixed')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        <Wrench className="w-3 h-3" />
        Auto-behoben
      </span>
    )
  if (status === 'issue')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
        <AlertTriangle className="w-3 h-3" />
        Problem
      </span>
    )
  if (status === 'error')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        Fehler
      </span>
    )
  return null
}

function AiScoreBadge({ score }: { score: number | null | undefined }) {
  if (score == null) return null
  const pct = Math.round(score * 100)
  const color =
    pct >= 85 ? 'bg-green-100 text-green-700' : pct >= 65 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>
      {pct} %
    </span>
  )
}

function renderFixValue(item: FixableItem): string {
  if (item.newValue == null) return '–'
  if (item.checkId === 'group_check' && item.newValue.startsWith('{')) {
    try {
      const parsed = JSON.parse(item.newValue) as { groupName?: string }
      return parsed.groupName ?? item.newValue
    } catch {
      return item.newValue
    }
  }
  if (item.checkId === 'content_standard') {
    try {
      const parsed = JSON.parse(item.newValue) as { title?: string; description?: string }
      const parts: string[] = []
      if (parsed.title) parts.push(parsed.title)
      if (parsed.description) {
        const desc = parsed.description
        parts.push(desc.length > 60 ? desc.slice(0, 60) + '…' : desc)
      }
      return parts.join(' | ') || item.newValue
    } catch {
      return item.newValue
    }
  }
  if (item.checkId === 'purchase_price_egis') {
    const num = Number(item.newValue)
    if (!isNaN(num)) return formatCurrency(num)
  }
  return item.newValue
}

function formatTs(ts: string | null | undefined) {
  if (!ts) return '–'
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function PriceDisplay({ cents }: { cents: string | null }) {
  if (!cents) return <span className="text-gray-400">–</span>
  const n = Number(cents)
  if (isNaN(n)) return <span className="text-xs text-gray-500">{cents}</span>
  return <span>{formatCurrency(n / 100)}</span>
}

// ─── Stat tiles ───────────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sub,
  color,
}: {
  label: string
  value: number | string
  sub?: string
  color?: string
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className={`text-2xl font-bold ${color ?? 'text-gray-900'}`}>{value}</div>
      <div className="text-sm font-medium text-gray-600 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  )
}

// ─── Run progress panel ───────────────────────────────────────────────────────

function RunProgressPanel({
  run,
  starting,
}: {
  run: VerificationRunSummary | null | undefined
  starting?: boolean
}) {
  const [visible, setVisible] = useState(false)
  const [prevStatus, setPrevStatus] = useState<string | null>(null)

  useEffect(() => {
    if (starting) {
      setVisible(true)
      return
    }
    if (!run) return
    if (run.status === 'running') {
      setVisible(true)
      setPrevStatus('running')
    } else if (prevStatus === 'running' && (run.status === 'done' || run.status === 'error')) {
      setPrevStatus(run.status)
      const t = setTimeout(() => setVisible(false), 6000)
      return () => clearTimeout(t)
    }
  }, [run?.status, prevStatus, starting])

  if (!visible) return null

  const pct =
    run && (run.totalArticles ?? 0) > 0
      ? Math.round(((run.checked ?? 0) / run.totalArticles) * 100)
      : 0
  const done = run?.status === 'done'
  const failed = run?.status === 'error'

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-3">
      {/* Title row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {done && <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />}
          {failed && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
          {!done && !failed && (
            <RefreshCw className="w-4 h-4 text-brand-500 shrink-0 animate-spin" />
          )}
          <span className="text-sm font-semibold text-gray-900">
            {done
              ? 'Prüfung abgeschlossen'
              : failed
                ? 'Prüfung fehlgeschlagen'
                : 'KI-Qualitätsprüfung läuft…'}
          </span>
        </div>
        <button
          onClick={() => setVisible(false)}
          className="text-gray-400 hover:text-gray-600 shrink-0"
        >
          <XCircle className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          {run ? (
            <>
              <span>
                {(run.checked ?? 0).toLocaleString('de-DE')} /{' '}
                {(run.totalArticles ?? 0).toLocaleString('de-DE')} Artikel
              </span>
              <span>{pct} %</span>
            </>
          ) : (
            <span className="text-gray-400">Prüfung wird gestartet…</span>
          )}
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              failed ? 'bg-red-400' : done ? 'bg-green-500' : 'bg-brand-500'
            } ${!run ? 'animate-pulse w-1/3' : ''}`}
            style={run ? { width: `${pct}%` } : undefined}
          />
        </div>
      </div>

      {/* Stats row */}
      {run && (
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            {run.issuesFound ?? 0} Problem{(run.issuesFound ?? 0) !== 1 ? 'e' : ''}
          </span>
          <span className="flex items-center gap-1">
            <Wrench className="w-3 h-3 text-green-600" />
            {run.autoFixed ?? 0} Auto-behoben
          </span>
          {run.error && <span className="text-red-500 truncate">{run.error}</span>}
        </div>
      )}
    </div>
  )
}

// ─── Main section ─────────────────────────────────────────────────────────────

type ViewTab = 'issues' | 'changes' | 'history'
type SeverityFilter = 'all' | 'error' | 'warn' | 'info'
type StatusFilter = 'all' | 'issue' | 'auto_fixed' | 'error'

export function AiVerificationSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [view, setView] = useState<ViewTab>('issues')
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [checkFilter, setCheckFilter] = useState<string>('all')
  const [issuesPage, setIssuesPage] = useState(1)
  const [changesPage, setChangesPage] = useState(1)
  const [historyExpanded, setHistoryExpanded] = useState<string | null>(null)
  const [showConfig, setShowConfig] = useState(false)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [stepRunning, setStepRunning] = useState(false)
  const stepAbortRef = useRef(false)
  const [showDiagnose, setShowDiagnose] = useState(false)
  const [batchScoreThreshold, setBatchScoreThreshold] = useState(0.8)
  const [batchFixRunning, setBatchFixRunning] = useState(false)
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null)
  const batchAbortRef = useRef(false)
  const [showBatchPreview, setShowBatchPreview] = useState(false)
  const [checkFixPending, setCheckFixPending] = useState<Record<string, boolean>>({})

  const { data: checkConfig, isLoading: configLoading } = useQuery<CheckConfig>({
    queryKey: ['article-ai-verification-config'],
    queryFn: () => apiClient.get('/articles/ai-verification/config').then((r) => r.data),
  })

  const configMutation = useMutation<CheckConfig, Error, CheckConfig>({
    mutationFn: (cfg) => apiClient.put('/articles/ai-verification/config', cfg).then((r) => r.data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['article-ai-verification-config'], updated)
      toast({ title: 'Prüf-Konfiguration gespeichert' })
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Speichern', description: err.message, tone: 'danger' })
    },
  })

  function toggleCheck(id: CheckId) {
    if (!checkConfig) return
    const updated = { ...checkConfig, [id]: !checkConfig[id] }
    configMutation.mutate(updated)
  }

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery<VerificationStats>({
    queryKey: ['article-ai-verification-stats'],
    queryFn: () => apiClient.get('/articles/ai-verification/stats').then((r) => r.data),
    refetchInterval: (query) => (query.state.data?.lastRun?.status === 'running' ? 3000 : 60000),
  })

  const noArticles = (stats?.articleCount?.total ?? 1) === 0
  const { data: lastSyncStatus } = useQuery<{
    running: boolean
    lastResult?: { error?: string; _fromDb?: boolean; _dbStatus?: string; _dbFinishedAt?: string | null }
  }>({
    queryKey: ['article-sync-status-for-ki'],
    queryFn: () => apiClient.get('/articles/sync/status').then((r) => r.data),
    enabled: noArticles,
    refetchInterval: noArticles ? 30000 : false,
  })

  const { data: issues, isLoading: issuesLoading } = useQuery<{
    data: VerificationIssue[]
    total: number
  }>({
    queryKey: [
      'article-ai-verification-issues',
      severityFilter,
      statusFilter,
      checkFilter,
      issuesPage,
    ],
    queryFn: () =>
      apiClient
        .get('/articles/ai-verification/issues', {
          params: {
            severity: severityFilter !== 'all' ? severityFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            checkId: checkFilter !== 'all' ? checkFilter : undefined,
            page: issuesPage,
            pageSize: 50,
          },
        })
        .then((r) => r.data),
    enabled: view === 'issues',
  })

  const { data: changes, isLoading: changesLoading } = useQuery<{
    data: VerificationChange[]
    total: number
  }>({
    queryKey: ['article-ai-verification-changes', changesPage],
    queryFn: () =>
      apiClient
        .get('/articles/ai-verification/changes', {
          params: { page: changesPage, pageSize: 50 },
        })
        .then((r) => r.data),
    enabled: view === 'changes',
  })

  const { data: runs, isLoading: runsLoading } = useQuery<VerificationRunSummary[]>({
    queryKey: ['article-ai-verification-runs'],
    queryFn: () => apiClient.get('/articles/ai-verification/runs?limit=20').then((r) => r.data),
    enabled: view === 'history',
  })

  const { data: diagnoseData, refetch: refetchDiagnose, isFetching: diagnoseLoading } = useQuery<{
    ok: boolean
    steps: Array<{ step: string; ok: boolean; detail: string }>
    error?: string
  }>({
    queryKey: ['article-ai-verification-diagnose'],
    queryFn: () => apiClient.get('/articles/ai-verification/diagnose').then((r) => r.data).catch((e: { response?: { data?: unknown }; message?: string }) => e.response?.data ?? { ok: false, steps: [], error: e.message }),
    enabled: showDiagnose,
    retry: false,
  })

  const { data: fixableItems, refetch: refetchFixable } = useQuery<{ data: FixableItem[]; total: number }>({
    queryKey: ['article-ai-verification-fixable', batchScoreThreshold],
    queryFn: () =>
      apiClient
        .get('/articles/ai-verification/fixable', { params: { minScore: batchScoreThreshold } })
        .then((r) => r.data),
    enabled: view === 'issues',
  })

  async function runBatchFix() {
    const initialTotal = fixableItems?.total ?? 0
    if (!initialTotal) return
    setBatchFixRunning(true)
    batchAbortRef.current = false
    setBatchProgress({ done: 0, total: initialTotal })

    let totalFixed = 0
    let totalSkipped = 0
    let totalErrors = 0

    while (!batchAbortRef.current) {
      try {
        const result = await apiClient
          .post('/articles/ai-verification/issues/batch-fix', {
            minScore: batchScoreThreshold,
            limit: 100,
          })
          .then(
            (r) =>
              r.data as {
                fixed: number
                skipped: number
                errors: number
                processedCount: number
                totalAvailable: number
              },
          )
        totalFixed += result.fixed
        totalSkipped += result.skipped
        totalErrors += result.errors
        setBatchProgress({ done: totalFixed + totalSkipped + totalErrors, total: initialTotal })
        if (result.processedCount === 0 || result.totalAvailable === 0) break
      } catch (err) {
        const axErr = err as { response?: { data?: { error?: string } }; message?: string }
        toast({
          title: 'Batch-Fix fehlgeschlagen',
          description: axErr.response?.data?.error ?? axErr.message ?? 'Unbekannter Fehler',
          tone: 'danger',
        })
        break
      }
    }

    setBatchFixRunning(false)
    setBatchProgress(null)
    void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-issues'] })
    void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-stats'] })
    void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-changes'] })
    void refetchFixable()

    const parts = [
      totalFixed ? `${totalFixed} behoben` : null,
      totalSkipped ? `${totalSkipped} übersprungen` : null,
      totalErrors ? `${totalErrors} Fehler` : null,
    ].filter(Boolean)
    toast({
      title: 'Batch-Fix abgeschlossen',
      description: parts.length ? parts.join(', ') : 'Keine Änderungen',
      tone: totalErrors ? 'warning' : 'success',
    })
  }

  async function fixCheckType(checkId: string) {
    setCheckFixPending((prev) => ({ ...prev, [checkId]: true }))
    let totalFixed = 0
    let totalSkipped = 0
    let totalErrors = 0
    try {
      while (true) {
        const result = await apiClient
          .post('/articles/ai-verification/issues/batch-fix', { checks: [checkId], limit: 100 })
          .then(
            (r) =>
              r.data as {
                fixed: number
                skipped: number
                errors: number
                processedCount: number
                totalAvailable: number
              },
          )
        totalFixed += result.fixed
        totalSkipped += result.skipped
        totalErrors += result.errors
        if (result.processedCount === 0 || result.totalAvailable === 0) break
      }
      const parts = [
        totalFixed ? `${totalFixed} behoben` : null,
        totalSkipped ? `${totalSkipped} übersprungen` : null,
        totalErrors ? `${totalErrors} Fehler` : null,
      ].filter(Boolean)
      toast({
        title: `${CHECK_LABELS[checkId] ?? checkId}: Fix abgeschlossen`,
        description: parts.length ? parts.join(', ') : 'Keine Änderungen',
        tone: totalErrors ? 'warning' : 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-issues'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-changes'] })
      void refetchFixable()
    } catch (err) {
      const axErr = err as { response?: { data?: { error?: string } }; message?: string }
      toast({
        title: 'Fix fehlgeschlagen',
        description: axErr.response?.data?.error ?? axErr.message ?? 'Unbekannter Fehler',
        tone: 'danger',
      })
    } finally {
      setCheckFixPending((prev) => ({ ...prev, [checkId]: false }))
    }
  }

  const runMutation = useMutation<{ runId: string; totalArticles: number; message: string }>({
    mutationFn: () =>
      apiClient.post('/articles/ai-verification/run', { autoFix: true }).then((r) => r.data),
    onSuccess: ({ runId }) => {
      setActiveRunId(runId)
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string }
      const msg = axiosErr.response?.data?.error ?? axiosErr.message ?? 'Unbekannter Fehler'
      toast({ title: 'Fehler beim Starten', description: msg, tone: 'danger' })
      setShowDiagnose(true)
    },
  })

  // Step loop: frontend drives article processing, one batch per request (Passenger-safe).
  useEffect(() => {
    if (!activeRunId) return

    stepAbortRef.current = false
    setStepRunning(true)

    async function runSteps(runId: string) {
      let done = false
      while (!done && !stepAbortRef.current) {
        try {
          const result = await apiClient
            .post(`/articles/ai-verification/run/${runId}/step`, { autoFix: true, batchSize: 25 })
            .then((r) => r.data as { checked: number; total: number; done: boolean })
          done = result.done
        } catch (err) {
          const axErr = err as { response?: { data?: { error?: string } }; message?: string }
          toast({
            title: 'Prüfung fehlgeschlagen',
            description: axErr.response?.data?.error ?? axErr.message ?? 'Unbekannter Fehler',
            tone: 'danger',
          })
          done = true
        }
      }
      setStepRunning(false)
      setActiveRunId(null)
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-runs'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-issues'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-changes'] })
    }

    void runSteps(activeRunId)

    return () => {
      stepAbortRef.current = true
    }
  }, [activeRunId, queryClient, toast])

  const lastRun = stats?.lastRun ?? null
  const isRunning = stepRunning || runMutation.isPending || lastRun?.status === 'running'
  const isStuckRun = !stepRunning && !runMutation.isPending && lastRun?.status === 'running' &&
    (Date.now() - new Date(lastRun.startedAt).getTime()) / 1000 / 60 >= 10

  const fixIssueMutation = useMutation<
    { fixed: boolean; newStatus: string; message: string },
    Error,
    { articleId: string; checkId: string }
  >({
    mutationFn: ({ articleId, checkId }) =>
      apiClient
        .post(`/articles/ai-verification/issues/${articleId}/${checkId}/fix`, {})
        .then((r) => r.data),
    onSuccess: (result) => {
      toast({
        title: result.fixed ? 'Auto-Behebung erfolgreich' : 'Keine Änderung',
        description: result.message,
        tone: result.fixed ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-issues'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['article-ai-verification-changes'] })
    },
    onError: (err) => {
      toast({ title: 'Behebung fehlgeschlagen', description: err.message, tone: 'danger' })
    },
  })

  // Issues columns
  const issueColumns: ColumnDef<VerificationIssue>[] = [
    {
      accessorKey: 'itemCode',
      header: 'Artikel',
      cell: ({ row }) => (
        <div>
          <div className="font-mono text-xs text-gray-500">{row.original.itemCode}</div>
          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
            {row.original.itemName}
          </div>
          {row.original.groupName && (
            <div className="text-xs text-gray-400">{row.original.groupName}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'checkId',
      header: 'Prüfung',
      cell: ({ row }) => (
        <span className="text-sm">
          {CHECK_LABELS[row.original.checkId] ?? row.original.checkId}
        </span>
      ),
    },
    {
      accessorKey: 'severity',
      header: 'Stufe',
      cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'message',
      header: 'Details',
      cell: ({ row }) => {
        const { oldValue, newValue, message, checkId } = row.original
        const isCentsCheck = checkId === 'purchase_price_egis' || checkId === 'sales_price_calculation'

        // group_check: newValue is JSON { groupName, groupCode, reason }
        if (checkId === 'group_check' && newValue && newValue.startsWith('{')) {
          try {
            const proposal = JSON.parse(newValue) as { groupName?: string; reason?: string }
            return (
              <div className="space-y-1 max-w-sm">
                <div className="flex items-start gap-1.5">
                  <div className="space-y-1 min-w-0">
                    {proposal.groupName && (
                      <div className="text-xs">
                        <span className="text-gray-500">Vorschlag: </span>
                        <span className="font-medium text-blue-700">{proposal.groupName}</span>
                      </div>
                    )}
                    {oldValue && (
                      <div className="text-xs text-gray-500">
                        Aktuell: {oldValue}
                      </div>
                    )}
                    {proposal.reason && (
                      <Tooltip content={<span className="text-xs leading-snug max-w-xs block">{proposal.reason}</span>} side="top">
                        <span className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-gray-600 cursor-default">
                          <Info className="w-3 h-3" />
                          KI-Begründung
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            )
          } catch {
            // fall through to default rendering
          }
        }

        // content_standard: newValue is JSON { title, description }
        if (checkId === 'content_standard' && newValue && newValue.startsWith('{')) {
          try {
            const proposal = JSON.parse(newValue) as { title?: string; description?: string }
            return (
              <div className="space-y-1 max-w-sm">
                <div className="flex items-start gap-1.5">
                  <Tooltip content={<span className="text-xs leading-snug">{message}</span>} side="top">
                    <span className="inline-flex items-center text-gray-400 hover:text-gray-600 cursor-default shrink-0 mt-0.5">
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </Tooltip>
                  <div className="space-y-1 min-w-0">
                    {proposal.title && (
                      <div className="text-xs">
                        <span className="text-gray-500">Titel: </span>
                        <span className="font-medium text-blue-700">{proposal.title}</span>
                      </div>
                    )}
                    {proposal.description && (
                      <div className="text-xs text-gray-600 leading-snug" title={proposal.description}>
                        <span className="text-gray-500">Beschr.: </span>
                        {proposal.description.length > 90
                          ? proposal.description.slice(0, 90) + '…'
                          : proposal.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          } catch {
            // fall through to default rendering
          }
        }

        function renderVal(v: string | null, bg: string) {
          if (!v) return null
          const content = isCentsCheck ? <PriceDisplay cents={v} /> : v
          return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${bg}`}>
              {content}
            </span>
          )
        }
        if (oldValue || newValue) {
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              {renderVal(oldValue, 'bg-gray-100 text-gray-700 border-gray-200')}
              {oldValue && newValue && (
                <span className="text-xs text-gray-400">→</span>
              )}
              {renderVal(newValue, 'bg-blue-50 text-blue-700 border-blue-200')}
              <Tooltip content={<span className="text-xs leading-snug">{message}</span>} side="top">
                <span className="inline-flex items-center text-gray-400 hover:text-gray-600 cursor-default">
                  <Info className="w-3.5 h-3.5" />
                </span>
              </Tooltip>
            </div>
          )
        }
        return <div className="text-sm text-gray-700 max-w-sm">{message}</div>
      },
    },
    {
      accessorKey: 'aiScore',
      header: 'KI-Score',
      cell: ({ row }) => <AiScoreBadge score={row.original.aiScore} />,
    },
    {
      accessorKey: 'checkedAt',
      header: 'Geprüft',
      cell: ({ row }) => (
        <span className="text-xs text-gray-400">{formatTs(row.original.checkedAt)}</span>
      ),
    },
    {
      id: 'autofix',
      header: '',
      cell: ({ row }) => {
        const issue = row.original
        if (issue.status === 'auto_fixed' || !FIXABLE_CHECKS.has(issue.checkId)) return null
        const isPending =
          fixIssueMutation.isPending &&
          fixIssueMutation.variables?.articleId === issue.articleId &&
          fixIssueMutation.variables?.checkId === issue.checkId
        return (
          <Button
            variant="secondary"
            size="xs"
            disabled={isPending}
            onClick={() =>
              fixIssueMutation.mutate({ articleId: issue.articleId, checkId: issue.checkId })
            }
          >
            <Wrench className="w-3 h-3 mr-1" />
            {isPending ? '…' : 'Beheben'}
          </Button>
        )
      },
    },
  ]

  // Changes columns
  const changeColumns: ColumnDef<VerificationChange>[] = [
    {
      accessorKey: 'changedAt',
      header: 'Zeitpunkt',
      cell: ({ row }) => (
        <span className="text-xs text-gray-500">{formatTs(row.original.changedAt)}</span>
      ),
    },
    {
      accessorKey: 'itemCode',
      header: 'Artikel',
      cell: ({ row }) => (
        <div>
          <div className="font-mono text-xs text-gray-500">{row.original.itemCode}</div>
          <div className="text-sm text-gray-900 truncate max-w-xs">{row.original.itemName}</div>
        </div>
      ),
    },
    {
      accessorKey: 'checkId',
      header: 'Prüfung',
      cell: ({ row }) => (
        <span className="text-sm">
          {CHECK_LABELS[row.original.checkId] ?? row.original.checkId}
        </span>
      ),
    },
    {
      accessorKey: 'field',
      header: 'Feld',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-gray-600">{row.original.field}</span>
      ),
    },
    {
      accessorKey: 'oldValue',
      header: 'Alt',
      cell: ({ row }) => {
        const v = row.original.oldValue
        if (!v) return <span className="text-gray-400 text-xs">–</span>
        const n = Number(v)
        if (!isNaN(n) && row.original.field.includes('cents')) return <PriceDisplay cents={v} />
        return <span className="text-xs text-gray-500">{v}</span>
      },
    },
    {
      accessorKey: 'newValue',
      header: 'Neu',
      cell: ({ row }) => {
        const v = row.original.newValue
        if (!v) return <span className="text-gray-400 text-xs">–</span>
        const n = Number(v)
        if (!isNaN(n) && row.original.field.includes('cents')) return <PriceDisplay cents={v} />
        return <span className="text-xs font-medium text-green-700">{v}</span>
      },
    },
  ]

  if (statsLoading) return <LoadingState label="Prüfstatus wird geladen…" />
  if (statsError)
    return <ErrorState title="Fehler beim Laden" description={(statsError as Error).message} />

  return (
    <>
      <RunProgressPanel run={lastRun} starting={runMutation.isPending || stepRunning} />
      <div className="space-y-6">
        {/* Header + action */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-brand-500" />
              KI-Qualitätsprüfung
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Automatische Prüfung aller Artikel auf Datenqualität, Preisstimmigkeit und EOL-Status
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowConfig((v) => !v)}>
              <Settings2 className="w-4 h-4 mr-1.5" />
              Prüfungen konfigurieren
            </Button>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="primary"
                size="sm"
                onClick={() => runMutation.mutate()}
                disabled={runMutation.isPending || isRunning || noArticles}
                title={noArticles ? 'Keine Artikel in der Datenbank – bitte zuerst Artikelabgleich ausführen' : undefined}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-1.5 ${runMutation.isPending || isRunning ? 'animate-spin' : ''}`}
                />
                {isRunning ? 'Prüfung läuft…' : 'Jetzt prüfen'}
              </Button>
              {isStuckRun && (
                <button
                  className="text-xs text-amber-600 underline hover:text-amber-700"
                  onClick={() => runMutation.mutate()}
                  disabled={runMutation.isPending}
                >
                  Lauf hängt – neu starten
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Diagnose panel — shown automatically when /run returns 500 */}
        {showDiagnose && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-red-800">System-Diagnose</span>
              <div className="flex gap-2">
                <button className="text-xs text-red-600 underline" onClick={() => void refetchDiagnose()}>
                  Erneut prüfen
                </button>
                <button className="text-xs text-gray-500 underline" onClick={() => setShowDiagnose(false)}>
                  Schließen
                </button>
              </div>
            </div>
            {diagnoseLoading && <p className="text-xs text-red-700">Diagnose läuft…</p>}
            {diagnoseData && (
              <div className="space-y-1">
                {diagnoseData.steps.map((s) => (
                  <div key={s.step} className="flex items-start gap-2 text-xs">
                    <span className={s.ok ? 'text-green-600' : 'text-red-700'}>
                      {s.ok ? '✓' : '✗'}
                    </span>
                    <span className="font-medium text-gray-700 w-52 shrink-0">{s.step}</span>
                    <span className={s.ok ? 'text-gray-500' : 'text-red-700 font-medium'}>{s.detail}</span>
                  </div>
                ))}
                {diagnoseData.error && (
                  <p className="text-xs text-red-700 mt-1 font-medium">{diagnoseData.error}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Check config panel */}
        {showConfig && (
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Aktive Prüfungen</span>
                <span className="text-xs text-gray-400">
                  {checkConfig
                    ? `${ALL_CHECK_IDS.filter((id) => checkConfig[id]).length}/${ALL_CHECK_IDS.length} aktiv`
                    : ''}
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              {configLoading ? (
                <LoadingState label="Konfiguration wird geladen…" />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ALL_CHECK_IDS.map((id) => {
                    const meta = CHECK_META[id]
                    const enabled = checkConfig?.[id] ?? true
                    return (
                      <button
                        key={id}
                        onClick={() => toggleCheck(id)}
                        disabled={configMutation.isPending}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          enabled
                            ? 'bg-white border-brand-200 hover:border-brand-400'
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300 opacity-60'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                          <span
                            className={`shrink-0 w-8 h-5 rounded-full transition-colors flex items-center px-0.5 ${
                              enabled ? 'bg-brand-500' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                enabled ? 'translate-x-3' : 'translate-x-0'
                              }`}
                            />
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 leading-snug">
                          {meta.description}
                        </p>
                        {meta.requiresExternal && (
                          <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                            {meta.requiresExternal}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Article DB diagnostic */}
        {stats?.articleCount !== undefined && (
          <div className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-3 ${
            stats.articleCount.total === 0
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${stats.articleCount.total === 0 ? 'text-amber-500' : 'text-blue-500'}`} />
            <div className="flex-1 min-w-0">
              {stats.articleCount.total === 0 ? (
                <>
                  <p className="font-medium">Keine Artikel in der Datenbank.</p>
                  <p className="mt-0.5">
                    Die KI-Prüfung greift auf die lokale MySQL-Datenbank zu (Artikel werden via SAP-Sync befüllt).
                    Bitte zunächst den Artikelabgleich unter <span className="font-medium">Einkauf → Stammdaten</span> ausführen.
                  </p>
                  {lastSyncStatus?.lastResult?.error && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-xs break-words">
                      <span className="font-semibold">Letzter Sync-Fehler: </span>
                      {lastSyncStatus.lastResult.error}
                    </div>
                  )}
                  {lastSyncStatus?.lastResult && !lastSyncStatus.lastResult.error && lastSyncStatus.lastResult._fromDb && (
                    <p className="mt-1 text-xs text-amber-700">
                      Letzter Sync abgeschlossen ({lastSyncStatus.lastResult._dbStatus ?? 'unbekannt'}
                      {lastSyncStatus.lastResult._dbFinishedAt
                        ? ` um ${new Date(lastSyncStatus.lastResult._dbFinishedAt).toLocaleTimeString('de-DE')}`
                        : ''}) — aber 0 Artikel. SAP-Verbindung und Zugangsdaten in den Einstellungen prüfen.
                    </p>
                  )}
                  <button
                    className="mt-2 text-xs text-amber-700 underline hover:text-amber-900"
                    onClick={() => void refetchStats()}
                  >
                    Artikelanzahl neu laden
                  </button>
                </>
              ) : (
                <>
                  <span className="font-medium">{stats.articleCount.total.toLocaleString('de-DE')} Artikel</span>
                  {' '}in lokaler DB
                  {stats.articleCount.inactive > 0 && (
                    <span className="text-blue-600">
                      {' '}({stats.articleCount.active.toLocaleString('de-DE')} aktiv, {stats.articleCount.inactive.toLocaleString('de-DE')} inaktiv — werden alle geprüft)
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile
            label="Probleme gesamt"
            value={stats?.totalIssues ?? 0}
            sub={
              lastRun
                ? `Stand: ${formatTs(lastRun.finishedAt ?? lastRun.startedAt)}`
                : 'Noch keine Prüfung'
            }
            color={stats?.totalIssues ? 'text-amber-600' : 'text-green-600'}
          />
          <StatTile
            label="Auto-behoben"
            value={stats?.totalAutoFixed ?? 0}
            color="text-green-600"
          />
          <StatTile
            label="Artikel geprüft"
            value={lastRun?.checked ?? 0}
            sub={lastRun ? `von ${lastRun.totalArticles}` : undefined}
          />
          <StatTile
            label="Letzte Prüfung"
            value={lastRun ? formatTs(lastRun.finishedAt ?? lastRun.startedAt) : '–'}
            color={!lastRun ? 'text-gray-400' : undefined}
          />
        </div>

        {/* Issues by check */}
        {stats?.issuesByCheck && stats.issuesByCheck.length > 0 && (
          <Card>
            <Card.Header>
              <h3 className="text-sm font-semibold text-gray-700">Probleme nach Prüfung</h3>
            </Card.Header>
            <Card.Body>
              <div className="flex flex-wrap gap-2">
                {stats.issuesByCheck.map((item) => {
                  const colorCls =
                    item.severity === 'error'
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : item.severity === 'warn'
                        ? 'bg-amber-50 border-amber-200 text-amber-700'
                        : 'bg-blue-50 border-blue-200 text-blue-700'
                  const isFixable = FIXABLE_CHECKS.has(item.checkId)
                  const isPending = checkFixPending[item.checkId] ?? false
                  return (
                    <div
                      key={item.checkId}
                      className={`inline-flex items-stretch rounded-lg border text-xs font-medium ${colorCls}`}
                    >
                      <button
                        onClick={() => {
                          setCheckFilter(item.checkId)
                          setView('issues')
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 hover:opacity-75 transition-opacity"
                      >
                        {CHECK_LABELS[item.checkId] ?? item.checkId}
                        <span className="bg-white rounded-full px-1.5 py-0.5 font-bold">
                          {item.count}
                        </span>
                      </button>
                      {isFixable && (
                        <button
                          onClick={() => void fixCheckType(item.checkId)}
                          disabled={isPending || batchFixRunning}
                          title={`Alle ${item.count} ${CHECK_LABELS[item.checkId] ?? item.checkId}-Probleme beheben`}
                          className="border-l border-current/20 px-2 py-1.5 flex items-center gap-1 hover:opacity-75 transition-opacity disabled:opacity-40"
                        >
                          <Wrench className="w-3 h-3" />
                          {isPending ? '…' : 'Alle'}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {[
            { key: 'issues' as ViewTab, label: 'Probleme', icon: AlertTriangle },
            { key: 'changes' as ViewTab, label: 'Audit-Log', icon: CheckCircle2 },
            { key: 'history' as ViewTab, label: 'Prüf-Verlauf', icon: History },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                view === key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Issues tab */}
        {view === 'issues' && (
          <div className="space-y-4">
            {/* Batch-fix panel */}
            {(fixableItems?.total ?? 0) > 0 && (
              <div className="rounded-lg border border-brand-200 bg-brand-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-brand-600 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-brand-900">
                        Batch-Fix: {fixableItems!.total} KI-Vorschlag{fixableItems!.total !== 1 ? 'e' : ''} anwendbar
                      </p>
                      <p className="text-xs text-brand-700 mt-0.5">
                        Alle Vorschläge mit KI-Score ≥ {Math.round(batchScoreThreshold * 100)} % automatisch übernehmen
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {batchFixRunning && batchProgress ? (
                      <span className="text-xs font-medium text-brand-700">
                        {batchProgress.done}/{batchProgress.total}
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <SlidersHorizontal className="w-3.5 h-3.5 text-brand-500" />
                        <input
                          type="range"
                          min={0.5}
                          max={1}
                          step={0.05}
                          value={batchScoreThreshold}
                          onChange={(e) => setBatchScoreThreshold(Number(e.target.value))}
                          className="w-24 accent-brand-500"
                        />
                        <span className="text-xs font-medium text-brand-700 w-8">
                          {Math.round(batchScoreThreshold * 100)} %
                        </span>
                      </div>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isRunning && !batchFixRunning}
                      onClick={() => {
                        if (batchFixRunning) {
                          batchAbortRef.current = true
                        } else {
                          void runBatchFix()
                        }
                      }}
                    >
                      <Zap className="w-3.5 h-3.5 mr-1.5" />
                      {batchFixRunning ? 'Abbrechen' : `${fixableItems!.total} beheben`}
                    </Button>
                  </div>
                </div>
                {/* Preview toggle */}
                {(fixableItems?.data?.length ?? 0) > 0 && (
                  <>
                    <button
                      onClick={() => setShowBatchPreview((v) => !v)}
                      className="flex items-center gap-1 mt-2 text-xs text-brand-600 hover:text-brand-800 transition-colors"
                    >
                      {showBatchPreview ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                      Vorschau {showBatchPreview ? 'ausblenden' : 'anzeigen'}
                    </button>
                    {showBatchPreview && (
                      <div className="mt-2 pt-2 border-t border-brand-200 max-h-64 overflow-y-auto space-y-0.5">
                        {fixableItems!.data.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 py-1 px-1 rounded text-xs hover:bg-brand-100/50"
                          >
                            <span className="font-medium text-brand-800 w-28 shrink-0 truncate">
                              {CHECK_LABELS[item.checkId] ?? item.checkId}
                            </span>
                            <span className="text-gray-500 truncate flex-1 min-w-0">
                              {item.itemName || item.itemCode}
                            </span>
                            <span className="text-gray-400 shrink-0">→</span>
                            <span className="text-gray-700 truncate max-w-[180px] shrink-0" title={item.newValue ?? undefined}>
                              {renderFixValue(item)}
                            </span>
                            <AiScoreBadge score={item.aiScore} />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <FilterBar>
              <FilterChip
                active={severityFilter !== 'all'}
                count={severityFilter !== 'all' ? undefined : undefined}
                onRemove={() => setSeverityFilter('all')}
              >
                <select
                  value={severityFilter}
                  onChange={(e) => {
                    setSeverityFilter(e.target.value as SeverityFilter)
                    setIssuesPage(1)
                  }}
                  className="text-sm bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="all">Alle Stufen</option>
                  <option value="error">Kritisch</option>
                  <option value="warn">Warnung</option>
                  <option value="info">Info</option>
                </select>
              </FilterChip>
              <FilterChip active={statusFilter !== 'all'} onRemove={() => setStatusFilter('all')}>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter)
                    setIssuesPage(1)
                  }}
                  className="text-sm bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="all">Alle Status</option>
                  <option value="issue">Problem offen</option>
                  <option value="auto_fixed">Auto-behoben</option>
                  <option value="error">Fehler</option>
                </select>
              </FilterChip>
              <FilterChip active={checkFilter !== 'all'} onRemove={() => setCheckFilter('all')}>
                <select
                  value={checkFilter}
                  onChange={(e) => {
                    setCheckFilter(e.target.value)
                    setIssuesPage(1)
                  }}
                  className="text-sm bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="all">Alle Prüfungen</option>
                  {Object.entries(CHECK_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </FilterChip>
            </FilterBar>

            {issuesLoading ? (
              <LoadingState label="Lade Probleme…" />
            ) : !issues?.data.length ? (
              <EmptyState
                title="Keine Probleme"
                description={
                  lastRun
                    ? 'Alle Artikel haben die Prüfung bestanden.'
                    : 'Noch keine Prüfung durchgeführt.'
                }
              />
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-gray-500">{issues.total} Einträge</div>
                <DataTable data={issues.data} columns={issueColumns} pagination={{ pageSize: 50 }} />
                {issues.total > 50 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-500">Seite {issuesPage}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setIssuesPage((p) => Math.max(1, p - 1))}
                        disabled={issuesPage <= 1}
                      >
                        ←
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setIssuesPage((p) => p + 1)}
                        disabled={issuesPage * 50 >= issues.total}
                      >
                        →
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Changes / Audit log tab */}
        {view === 'changes' && (
          <div className="space-y-4">
            {changesLoading ? (
              <LoadingState label="Lade Änderungen…" />
            ) : !changes?.data.length ? (
              <EmptyState
                title="Keine automatischen Änderungen"
                description="Es wurden noch keine Daten automatisch korrigiert."
              />
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-gray-500">
                  {changes.total} Änderungen protokolliert
                </div>
                <DataTable data={changes.data} columns={changeColumns} pagination={{ pageSize: 50 }} />
                {changes.total > 50 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-500">Seite {changesPage}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setChangesPage((p) => Math.max(1, p - 1))}
                        disabled={changesPage <= 1}
                      >
                        ←
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setChangesPage((p) => p + 1)}
                        disabled={changesPage * 50 >= changes.total}
                      >
                        →
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* History tab */}
        {view === 'history' && (
          <div className="space-y-2">
            {runsLoading ? (
              <LoadingState label="Lade Verlauf…" />
            ) : !runs?.length ? (
              <EmptyState title="Kein Verlauf" description="Noch kein Prüflauf durchgeführt." />
            ) : (
              runs.map((run) => (
                <div key={run.runId} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() =>
                      setHistoryExpanded(historyExpanded === run.runId ? null : run.runId)
                    }
                  >
                    <div className="flex items-center gap-3">
                      {run.status === 'done' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : run.status === 'error' ? (
                        <XCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                      )}
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {formatTs(run.startedAt)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {run.checked ?? 0}/{run.totalArticles ?? 0} Artikel ·{' '}
                          {run.issuesFound ?? 0} Probleme · {run.autoFixed ?? 0} auto-behoben
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          run.status === 'done'
                            ? 'bg-green-100 text-green-700'
                            : run.status === 'error'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {run.status === 'done'
                          ? 'Abgeschlossen'
                          : run.status === 'error'
                            ? 'Fehler'
                            : 'Läuft'}
                      </span>
                      {historyExpanded === run.runId ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                  {historyExpanded === run.runId && (
                    <div className="px-4 py-3 border-t border-gray-100 space-y-1 text-sm">
                      <div className="flex gap-6">
                        <div>
                          <span className="text-gray-500">Gestartet:</span>{' '}
                          <span className="font-medium">{formatTs(run.startedAt)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Beendet:</span>{' '}
                          <span className="font-medium">{formatTs(run.finishedAt)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Laufzeit:</span>{' '}
                          <span className="font-medium">
                            {run.finishedAt
                              ? `${Math.round((new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000)}s`
                              : '–'}
                          </span>
                        </div>
                      </div>
                      {run.error && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-red-600 text-xs font-mono">
                          {run.error}
                        </div>
                      )}
                      <div className="mt-2">
                        <Clock className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
                        <span className="text-gray-500 text-xs">
                          Run-ID: <span className="font-mono">{run.runId}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  )
}
